/**
 * Header behaviour.
 *
 * <sticky-header>      fixed header layer: pinned with only the nav row left once the page is
 *                      scrolled, or hidden on scroll down and revealed on scroll up; the
 *                      transparent state over the homepage hero, the logo that tucks into its
 *                      paw, runtime offsets, dropdown state. Keeps the hooks other scripts use:
 *                      reveal(), hide(), preventHide and the "preventHeaderReveal" event.
 * <fable-menu-drawer>  mobile menu drawer (dialog with focus trap).
 * <fable-announcement> announcement bar; with several messages they rotate, with previous /
 *                      next / pause-play buttons.
 *
 * Runtime variables written on <html>:
 *   --fable-header-offset        height of the header layer in px
 *   --fable-header-shown-offset  how much of the layer covers the page: 0px while it is hidden
 *                                (or not sticky), the nav row while it is compact, else the offset
 *   --header-height              nav row height (stock scripts read it)
 * Classes written on <html>: fable-header-overlay, fable-header-is-hidden. On the layer:
 * fable-header-group--hidden, --compact, --instant, --menu-open, --static. On <sticky-header>:
 * fable-header--transparent, --condensed, --collapsed.
 *
 * Uses trapFocus / removeTrapFocus from global.js (loaded before this file, both deferred).
 */
(function () {
  const root = document.documentElement;
  const GROUP_HIDDEN = 'fable-header-group--hidden';
  const GROUP_MENU_OPEN = 'fable-header-group--menu-open';
  const GROUP_COMPACT = 'fable-header-group--compact';
  const GROUP_INSTANT = 'fable-header-group--instant';
  const CONDENSED = 'fable-header--condensed';
  const SCROLLED_AT = 80; // px scrolled before the header takes its scrolled state
  const TOP_AT = 20; // px from the top where it opens again; the gap stops flicker around one position
  const LOGO_SEQUENCE = 700; // ms the logo takes to tuck or open from 1000px
  const LOGO_SEQUENCE_COMPACT = 400; // and below it, where the wordmark cross-fades to the paw
  const INTENT_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
  const WORDMARK_MIN_SIZE = 11; // px; the floor of the CSS clamp on .fable-header__wordmark
  const desktop = window.matchMedia('(min-width: 1000px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const noHover = window.matchMedia('(hover: none)');
  const inDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  // A reload, a back/forward navigation or a #fragment moves the page by itself, at any moment up
  // to the load event. A plain navigation starts at the top and only the shopper moves it.
  const navigationEntry =
    window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
  const pageMovesItself = Boolean((navigationEntry && navigationEntry.type !== 'navigate') || window.location.hash);

  if (!customElements.get('sticky-header')) {
    customElements.define(
      'sticky-header',
      class StickyHeader extends HTMLElement {
        connectedCallback() {
          this.group = document.getElementById('FableHeaderGroup') || this.closest('.shopify-section') || this;
          this.bar = this.querySelector('.fable-header__bar');
          this.overlay = this.dataset.overlay === 'true';
          this.stickyType = this.dataset.stickyType || 'on-scroll-up';
          this.hideOnScroll = this.stickyType === 'on-scroll-up';
          this.condenses = this.dataset.logoMotion === 'true';
          this.searchModal = this.querySelector('details-modal');
          this.predictiveSearch = this.querySelector('predictive-search');
          this.lastY = Math.max(0, window.scrollY);
          this.upAnchor = this.lastY;
          this.preventHide = false;
          this.preventReveal = false;
          this.keepAnnouncement = false;
          this.ticking = false;
          this.scrolled = false;
          this.settled = false;
          this.logoFlippedAt = -Infinity;

          // the layer outlives a section re-render in the theme editor: start from a clean state
          this.group.classList.remove(GROUP_HIDDEN, GROUP_MENU_OPEN, GROUP_COMPACT, GROUP_INSTANT);
          this.group.classList.toggle('fable-header-group--static', this.stickyType === 'none');
          root.classList.remove('fable-header-is-hidden');
          root.classList.toggle('fable-header-overlay', this.overlay);

          this.onScroll = () => {
            if (this.ticking) return;
            this.ticking = true;
            requestAnimationFrame(() => {
              this.ticking = false;
              this.handleScroll();
            });
          };
          this.onPreventReveal = () => {
            this.preventReveal = true;
          };
          this.onFocusIn = () => this.reveal();
          this.onSearchToggle = () => {
            this.syncSearchLock();
            this.updateState();
          };
          this.onBreakpoint = () => this.syncSearchLock();
          this.onDocumentClick = (event) => this.closeDropdowns(event.target);
          this.onDocumentKeyDown = (event) => {
            if (event.code === 'Escape') this.dismissDropdowns();
          };
          this.onEditorSelect = (event) => {
            if (!this.group.contains(event.target)) return;
            this.preventHide = true;
            // a selected announcement stays in view; selecting the header itself still previews
            // what the storefront does on scroll
            this.keepAnnouncement = Boolean(event.target.closest('.announcement-bar-section'));
            this.reveal();
            this.updateState();
          };
          this.onEditorDeselect = () => {
            this.preventHide = false;
            this.keepAnnouncement = false;
            this.updateState();
          };
          this.onEditorReorder = () => this.updateState();
          this.settle = () => {
            this.settled = true;
            window.removeEventListener('load', this.onLoad);
            INTENT_EVENTS.forEach((type) => window.removeEventListener(type, this.settle));
          };
          // two frames, so the scroll event of a restore that lands with "load" is handled first
          this.onLoad = () => requestAnimationFrame(() => requestAnimationFrame(this.settle));

          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.addEventListener('preventHeaderReveal', this.onPreventReveal);
          this.group.addEventListener('focusin', this.onFocusIn);
          document.addEventListener('click', this.onDocumentClick);
          document.addEventListener('keydown', this.onDocumentKeyDown);
          desktop.addEventListener('change', this.onBreakpoint);

          // An attribute observer rather than the "toggle" event: it runs in the same task as
          // the click that opened the panel, so the page never paints with the stock scroll lock.
          this.searchDetails = this.searchModal && this.searchModal.querySelector('details');
          if (this.searchDetails) {
            this.searchObserver = new MutationObserver(this.onSearchToggle);
            this.searchObserver.observe(this.searchDetails, { attributes: true, attributeFilter: ['open'] });
          }

          if (inDesignMode()) {
            document.addEventListener('shopify:section:select', this.onEditorSelect);
            document.addEventListener('shopify:block:select', this.onEditorSelect);
            document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
            document.addEventListener('shopify:block:deselect', this.onEditorDeselect);
            // a section dragged within the layer changes whether the nav row is its last one
            document.addEventListener('shopify:section:reorder', this.onEditorReorder);
          }

          this.initDropdowns();

          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.setOffsets());
            this.resizeObserver.observe(this.group);
          }
          // settled is still false here: the first state is taken without the animations
          this.setOffsets();
          // Until the page has stopped moving by itself, a scroll is taken the same way. The
          // shopper's own first scroll ends that early: a restore never follows their input.
          if (document.readyState === 'complete' || !pageMovesItself) {
            this.settled = true;
          } else {
            window.addEventListener('load', this.onLoad);
            INTENT_EVENTS.forEach((type) => window.addEventListener(type, this.settle, { passive: true }));
          }
          // The web fonts change the wordmark and menu widths once they arrive. The font stylesheet
          // is not render-blocking, so fonts.ready can resolve before the fonts are even requested:
          // "loadingdone" covers every later batch of font loads.
          this.onFontsLoaded = () => this.isConnected && this.setOffsets();
          if (document.fonts) {
            if (document.fonts.ready) document.fonts.ready.then(this.onFontsLoaded);
            if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', this.onFontsLoaded);
          }
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('load', this.onLoad);
          INTENT_EVENTS.forEach((type) => window.removeEventListener(type, this.settle));
          clearTimeout(this.logoTimer);
          this.removeEventListener('preventHeaderReveal', this.onPreventReveal);
          if (this.group) this.group.removeEventListener('focusin', this.onFocusIn);
          document.removeEventListener('click', this.onDocumentClick);
          document.removeEventListener('keydown', this.onDocumentKeyDown);
          desktop.removeEventListener('change', this.onBreakpoint);
          document.removeEventListener('shopify:section:select', this.onEditorSelect);
          document.removeEventListener('shopify:block:select', this.onEditorSelect);
          document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
          document.removeEventListener('shopify:block:deselect', this.onEditorDeselect);
          document.removeEventListener('shopify:section:reorder', this.onEditorReorder);
          if (this.searchObserver) this.searchObserver.disconnect();
          if (this.resizeObserver) this.resizeObserver.disconnect();
          if (document.fonts && document.fonts.removeEventListener && this.onFontsLoaded) {
            document.fonts.removeEventListener('loadingdone', this.onFontsLoaded);
          }
          clearTimeout(this.revealTimer);
        }

        get menuOpen() {
          return this.group.classList.contains(GROUP_MENU_OPEN);
        }

        get searchOpen() {
          return Boolean(this.searchDetails && this.searchDetails.hasAttribute('open'));
        }

        get isHidden() {
          return this.group.classList.contains(GROUP_HIDDEN);
        }

        setOffsets() {
          // while the drawer is open the layer is stretched over the viewport
          if (this.menuOpen || !this.bar) return;
          this.fitWordmark();
          this.checkFit();
          const offset = `${this.group.offsetHeight}px`;
          const height = `${this.bar.offsetHeight}px`;
          // The compact layer's top is made of these two: a correction (the announcement wraps
          // onto another line, a font arrives) is taken at once rather than slid into place.
          const jump =
            this.group.classList.contains(GROUP_COMPACT) &&
            (offset !== root.style.getPropertyValue('--fable-header-offset') ||
              height !== root.style.getPropertyValue('--header-height'));
          if (jump) this.group.classList.add(GROUP_INSTANT);
          root.style.setProperty('--fable-header-offset', offset);
          root.style.setProperty('--header-height', height);
          if (jump) {
            void this.group.offsetWidth;
            this.group.classList.remove(GROUP_INSTANT);
          }
          this.updateState();
        }

        /* The text wordmark is sized by CSS from the number of characters. A name set in wide
           glyphs, or a very long one, can still be too long for its slot: step the size down to
           the floor, then tighten the tracking, then let it wrap, so the name is never cut off. */
        fitWordmark() {
          const wordmark = this.querySelector('.fable-header__wordmark');
          if (!wordmark) return;
          wordmark.style.fontSize = '';
          wordmark.classList.remove('fable-header__wordmark--tight', 'fable-header__wordmark--wrap');
          // scrollWidth is a whole number: a name that is a fraction of a pixel too wide would
          // still draw the ellipsis, so the text itself is measured as well
          const range = document.createRange();
          range.selectNodeContents(wordmark);
          const overflows = () =>
            wordmark.scrollWidth > wordmark.clientWidth ||
            range.getBoundingClientRect().width > wordmark.getBoundingClientRect().width + 0.05;
          let size = parseFloat(getComputedStyle(wordmark).fontSize);
          while (overflows() && size > WORDMARK_MIN_SIZE) {
            size = Math.max(WORDMARK_MIN_SIZE, size - 0.5);
            wordmark.style.fontSize = `${size}px`;
          }
          if (overflows()) wordmark.classList.add('fable-header__wordmark--tight');
          if (overflows()) wordmark.classList.add('fable-header__wordmark--wrap');
        }

        /* <details-modal> locks page scroll whenever the search opens. That suits the
           full-height drawer below 1000px; from 1000px the panel is a block under the nav row
           with no scrim, and the page behind it keeps scrolling. */
        syncSearchLock() {
          if (this.searchOpen) document.body.classList.toggle('overflow-hidden', !desktop.matches);
        }

        /* Inline menus that would run into the wordmark fall back to the hamburger + drawer. */
        checkFit() {
          const logo = this.querySelector('.fable-header__logo');
          if (!logo) return;
          this.classList.remove('fable-header--collapsed');
          if (!desktop.matches) return;
          const gap = 16;
          const logoRect = logo.getBoundingClientRect();
          const startNav = this.querySelector('.fable-header__start .fable-header__nav');
          const endFirst = this.querySelector('.fable-header__end > :not(.fable-header__block-anchor)');
          const startOverlaps = startNav && startNav.getBoundingClientRect().right > logoRect.left - gap;
          const endOverlaps = endFirst && endFirst.getBoundingClientRect().left < logoRect.right + gap;
          if (startOverlaps || endOverlaps) this.classList.add('fable-header--collapsed');
        }

        handleScroll() {
          const y = Math.max(0, window.scrollY);
          if (y > this.lastY) {
            // scrolling down
            this.upAnchor = y;
            const busy =
              this.preventHide ||
              this.menuOpen ||
              this.searchOpen ||
              (this.predictiveSearch && this.predictiveSearch.isOpen);
            if (this.hideOnScroll && y > 50 && !busy) this.hide();
          } else if (y < this.lastY) {
            // scrolling up: reveal near the top or after more than 50px of upward travel
            if (this.preventReveal) {
              clearTimeout(this.revealTimer);
              this.revealTimer = setTimeout(() => {
                this.preventReveal = false;
              }, 66);
              this.upAnchor = y;
            } else if (y <= 50 || this.upAnchor - y > 50) {
              this.reveal();
            }
          }
          this.lastY = y;
          this.updateState();
        }

        updateState() {
          const y = Math.max(0, window.scrollY);
          const transparent = this.overlay && y <= 150 && !this.menuOpen && !this.searchOpen;
          this.classList.toggle('fable-header--transparent', transparent);
          // one "scrolled" state for the layer and the logo
          this.scrolled = y > (this.scrolled ? TOP_AT : SCROLLED_AT);
          // Pinned header, away from the top: only the nav row stays. The shift is the height of
          // everything else in the layer, so it only applies while the nav row is the last section
          // (checked every time: the editor reorders sections without reconnecting this element).
          const compact =
            this.scrolled &&
            this.stickyType === 'always' &&
            !this.keepAnnouncement &&
            this.group.lastElementChild === this.closest('.shopify-section');
          const condensed = this.scrolled && this.condenses && !reducedMotion.matches;
          // first state and scroll restoration: taken without the animations
          const instant =
            !this.settled &&
            (compact !== this.group.classList.contains(GROUP_COMPACT) ||
              condensed !== this.classList.contains(CONDENSED));
          if (instant) this.group.classList.add(GROUP_INSTANT);
          this.group.classList.toggle(GROUP_COMPACT, compact);
          let shown = 'var(--fable-header-offset)';
          if (this.isHidden || this.stickyType === 'none') shown = '0px';
          else if (compact) shown = 'var(--header-height)';
          root.style.setProperty('--fable-header-shown-offset', shown);
          this.syncLogo(condensed);
          if (instant) {
            // flush the styles so the state is in place before the transitions come back
            void this.group.offsetWidth;
            this.group.classList.remove(GROUP_INSTANT);
          }
        }

        /* The logo tucks into its paw while the page is scrolled. The scroll position alone
           decides: an open drawer or search panel and focus inside the header change nothing.
           Not with reduced motion. Once the page has settled, a run finishes before the reverse
           one starts: the two directions have different delays, and a flip mid-run would hold
           parts where they are. */
        syncLogo(condensed) {
          this.logoWanted = condensed;
          clearTimeout(this.logoTimer);
          if (condensed === this.classList.contains(CONDENSED)) return;
          if (this.settled) {
            const sequence = desktop.matches ? LOGO_SEQUENCE : LOGO_SEQUENCE_COMPACT;
            const wait = this.logoFlippedAt + sequence - performance.now();
            if (wait > 0) {
              this.logoTimer = setTimeout(() => this.syncLogo(this.logoWanted), wait);
              return;
            }
            this.logoFlippedAt = performance.now();
          }
          this.classList.toggle(CONDENSED, condensed);
        }

        hide() {
          if (this.isHidden || !this.hideOnScroll) return;
          // also closes a dropdown that was opened by keyboard focus
          if (this.group.contains(document.activeElement)) document.activeElement.blur();
          this.closeDropdowns();
          this.group.classList.add(GROUP_HIDDEN);
          root.classList.add('fable-header-is-hidden');
          this.updateState();
        }

        reveal() {
          if (!this.isHidden) return;
          this.group.classList.remove(GROUP_HIDDEN);
          root.classList.remove('fable-header-is-hidden');
          this.updateState();
        }

        /* Dropdowns open with CSS (:hover / :focus-within); this mirrors the state to
           aria-expanded and gives touch screens a first-tap-opens rule. Escape is handled for
           the whole document (dismissDropdowns): a dropdown opened by the pointer has no focus
           inside it to receive the key. */
        initDropdowns() {
          this.querySelectorAll('.fable-header__item--has-dropdown').forEach((item) => {
            const link = item.querySelector(':scope > .fable-header__link');
            if (!link) return;
            const setExpanded = (expanded) => link.setAttribute('aria-expanded', String(expanded));
            const reset = () => {
              if (item.matches(':hover') || item.contains(document.activeElement)) return;
              item.classList.remove('is-dismissed', 'is-open');
              setExpanded(false);
            };

            item.addEventListener('mouseenter', () => {
              if (noHover.matches) return;
              // the pointer coming back is a new request to open
              item.classList.remove('is-dismissed');
              setExpanded(true);
            });
            item.addEventListener('mouseleave', () => setTimeout(reset));
            item.addEventListener('focusin', () => {
              if (!item.classList.contains('is-dismissed')) setExpanded(true);
            });
            item.addEventListener('focusout', () => setTimeout(reset));
            link.addEventListener('click', (event) => {
              if (!noHover.matches || item.classList.contains('is-open')) return;
              event.preventDefault();
              this.closeDropdowns();
              item.classList.remove('is-dismissed');
              item.classList.add('is-open');
              setExpanded(true);
            });
          });
        }

        /* Escape: close every dropdown that is showing, whether the pointer, keyboard focus or a
           tap opened it. .is-dismissed overrides :hover and :focus-within until the pointer and
           the focus have both left the item, or the pointer comes back to it (initDropdowns). */
        dismissDropdowns() {
          this.querySelectorAll('.fable-header__item--has-dropdown').forEach((item) => {
            const hasFocus = item.contains(document.activeElement);
            if (!hasFocus && !item.matches(':hover') && !item.classList.contains('is-open')) return;
            const link = item.querySelector(':scope > .fable-header__link');
            item.classList.add('is-dismissed');
            item.classList.remove('is-open');
            if (!link) return;
            link.setAttribute('aria-expanded', 'false');
            if (hasFocus) link.focus();
          });
        }

        closeDropdowns(except) {
          this.querySelectorAll('.fable-header__item--has-dropdown.is-open').forEach((item) => {
            if (except && item.contains(except)) return;
            item.classList.remove('is-open');
            const link = item.querySelector(':scope > .fable-header__link');
            if (link) link.setAttribute('aria-expanded', 'false');
          });
        }
      }
    );
  }

  if (!customElements.get('fable-menu-drawer')) {
    customElements.define(
      'fable-menu-drawer',
      class FableMenuDrawer extends HTMLElement {
        connectedCallback() {
          this.toggle = document.querySelector(`[aria-controls="${this.id}"]`);
          this.closeButton = this.querySelector('.fable-menu-drawer__close');
          this.header = this.closest('sticky-header');
          this.group = document.getElementById('FableHeaderGroup') || this.closest('.shopify-section');
          if (!this.toggle || !this.group) return;

          this.onToggleClick = () => (this.isOpen ? this.close() : this.open());
          this.onCloseClick = () => this.close();
          this.onKeyUp = (event) => {
            if (event.code === 'Escape') this.close();
          };
          // anything outside the panel is the scrim
          this.onGroupClick = (event) => {
            if (this.isOpen && !this.contains(event.target) && !this.toggle.contains(event.target)) this.close();
          };
          this.onBreakpoint = () => {
            const collapsed = this.header && this.header.classList.contains('fable-header--collapsed');
            if (desktop.matches && !collapsed && this.isOpen) this.close(false);
          };

          // theme editor: show the drawer while one of its menu blocks is selected
          this.onBlockSelect = () => {
            if (getComputedStyle(this).display !== 'none') this.open(false);
          };
          this.onBlockDeselect = () => this.close(false);

          this.toggle.addEventListener('click', this.onToggleClick);
          if (this.closeButton) this.closeButton.addEventListener('click', this.onCloseClick);
          this.addEventListener('keyup', this.onKeyUp);
          this.addEventListener('shopify:block:select', this.onBlockSelect);
          this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
          this.group.addEventListener('click', this.onGroupClick);
          desktop.addEventListener('change', this.onBreakpoint);
        }

        disconnectedCallback() {
          if (this.isOpen) this.close(false);
          if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
          if (this.group) this.group.removeEventListener('click', this.onGroupClick);
          desktop.removeEventListener('change', this.onBreakpoint);
        }

        get isOpen() {
          return this.classList.contains('is-open');
        }

        open(moveFocus = true) {
          if (this.isOpen) return;
          if (this.header && this.header.reveal) this.header.reveal();
          this.classList.add('is-open');
          this.setAttribute('aria-hidden', 'false');
          this.toggle.setAttribute('aria-expanded', 'true');
          this.group.classList.add(GROUP_MENU_OPEN);
          document.body.classList.add('overflow-hidden');
          if (this.header && this.header.updateState) this.header.updateState();
          this.scrollTop = 0;
          if (moveFocus && typeof trapFocus === 'function') {
            const target = this.closeButton || this;
            trapFocus(this, target);
            this.retryFocus(target, 0);
          }
        }

        // With reduced motion every property gets a .01ms transition, so the drawer's visibility
        // reaches its children a frame or two late and the first focus() can miss: try again briefly.
        retryFocus(target, attempt) {
          clearTimeout(this.focusTimer);
          if (!this.isOpen || attempt > 10 || this.contains(document.activeElement)) return;
          this.focusTimer = setTimeout(() => {
            if (!this.isOpen || this.contains(document.activeElement)) return;
            target.focus();
            this.retryFocus(target, attempt + 1);
          }, 40);
        }

        close(restoreFocus = true) {
          if (!this.isOpen) return;
          clearTimeout(this.focusTimer);
          this.classList.remove('is-open');
          this.setAttribute('aria-hidden', 'true');
          this.toggle.setAttribute('aria-expanded', 'false');
          this.group.classList.remove(GROUP_MENU_OPEN);
          document.body.classList.remove('overflow-hidden');
          if (typeof removeTrapFocus === 'function') removeTrapFocus(restoreFocus ? this.toggle : null);
          if (this.header && this.header.setOffsets) this.header.setOffsets();
        }
      }
    );
  }

  if (!customElements.get('fable-announcement')) {
    customElements.define(
      'fable-announcement',
      class FableAnnouncement extends HTMLElement {
        connectedCallback() {
          this.messages = Array.from(this.querySelectorAll('.fable-announcement__message'));
          this.region = this.querySelector('.fable-announcement__messages');
          this.toggleButton = this.querySelector('[data-action="toggle"]');
          this.index = Math.max(0, this.messages.findIndex((message) => message.classList.contains('is-active')));
          this.seconds = parseInt(this.dataset.rotate, 10) || 0;
          this.paused = false; // hover or focus inside the bar, or a block selected in the editor
          // stopped by the pause button; with reduced motion the bar starts stopped
          this.setStopped(reducedMotion.matches);

          this.onClick = (event) => {
            const button = event.target.closest('[data-action]');
            if (!button || !this.contains(button)) return;
            const count = this.messages.length;
            if (button.dataset.action === 'previous') this.step((this.index - 1 + count) % count);
            else if (button.dataset.action === 'next') this.step((this.index + 1) % count);
            else if (button.dataset.action === 'toggle') this.setStopped(!this.stopped);
          };
          this.onPause = () => {
            this.paused = true;
          };
          this.onResume = () => {
            this.paused = false;
          };
          this.onBlockSelect = (event) => {
            const index = this.messages.indexOf(event.target);
            if (index < 0) return;
            this.paused = true;
            this.show(index);
          };
          this.onBlockDeselect = () => {
            this.paused = false;
          };

          this.addEventListener('click', this.onClick);
          this.addEventListener('mouseenter', this.onPause);
          this.addEventListener('mouseleave', this.onResume);
          this.addEventListener('focusin', this.onPause);
          this.addEventListener('focusout', this.onResume);
          this.addEventListener('shopify:block:select', this.onBlockSelect);
          this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        // (re)starts the countdown, so a message picked by hand gets its full time
        startTimer() {
          clearInterval(this.timer);
          if (this.messages.length < 2 || this.seconds <= 0 || this.stopped) return;
          this.timer = setInterval(() => {
            if (!this.paused && !document.hidden) this.show((this.index + 1) % this.messages.length);
          }, this.seconds * 1000);
        }

        setStopped(stopped) {
          this.stopped = stopped;
          this.classList.toggle('fable-announcement--stopped', stopped);
          if (this.toggleButton) {
            this.toggleButton.setAttribute(
              'aria-label',
              stopped ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause
            );
          }
          // while it rotates by itself the bar stays quiet; once stopped, a message picked with
          // the arrows is read out
          if (this.region && this.messages.length > 1) {
            this.region.setAttribute('aria-live', stopped ? 'polite' : 'off');
          }
          if (stopped) clearInterval(this.timer);
          else this.startTimer();
        }

        step(index) {
          this.show(index);
          this.startTimer();
        }

        show(index) {
          if (index === this.index || !this.messages[index]) return;
          this.messages.forEach((message, i) => {
            const active = i === index;
            message.classList.toggle('is-active', active);
            if (active) message.removeAttribute('aria-hidden');
            else message.setAttribute('aria-hidden', 'true');
            if (active && message.dataset.scheme) {
              // the bar and its buttons take the colours of the visible message
              Array.from(this.classList)
                .filter((name) => name.startsWith('color-'))
                .forEach((name) => this.classList.remove(name));
              this.classList.add(`color-${message.dataset.scheme}`);
            }
          });
          this.index = index;
        }
      }
    );
  }
})();
